import React from 'react';
import PropTypes from 'prop-types';
import Cookies from 'cookies-js';
import classnames from 'classnames';

import { setWindowLocation } from '@eb/http';
import gettext from '@eb/gettext';
import {
    setAction,
    setFollowCollectionStatus,
    getFollowCollectionStatus,
} from '@eb/redux-destination';
import { withOverlayControls } from '@eb/eds-structure';
import { FollowButton } from '@eb/eds-follow-button';

import {
    GDPR_ACCEPT_GA_ACTION,
    GDPR_REJECT_GA_ACTION,
    GDPR_TITLE_TEXT,
    GDPR_MESSAGE_TEXT,
    GDPR_ACCEPT_TEXT,
    GDPR_REJECT_TEXT,
} from '../constants';

import './followCTA.scss';

const explicitOptIn = 1;
const explicitOptOut = 2;
const explicitChoices = [explicitOptIn, explicitOptOut];

export const hasAlreadyChosen = async (userId) => {
    let hasChosen = false;
    try {
        const response = await getFollowCollectionStatus(userId);
        hasChosen =
            explicitChoices.includes(
                response['preferences'][0]['global_preference'],
            ) &&
            explicitChoices.includes(
                response['preferences'][1]['global_preference'],
            );
    } catch (error) {
        // If the api fails we should still show the GDPR modal.
    }
    return !hasChosen;
};

export const shouldShowGDPRModal = async (isGDPRCountry = true, userId) => {
    if (!isGDPRCountry) {
        return false;
    }
    return hasAlreadyChosen(userId).then((hasChosen) => hasChosen);
};

const setFollowCollection = (userId, isOptedIn) =>
    setFollowCollectionStatus(userId, isOptedIn);

const getPendingCookieName = (collectionId) => `follow${collectionId}`;
export class FollowCTA extends React.Component {
    static propTypes = {
        handleFollowCollection: PropTypes.func.isRequired,
        handleUnfollowCollection: PropTypes.func.isRequired,
        isFollowing: PropTypes.bool,
        isPublic: PropTypes.bool,
        userId: PropTypes.string,
        collectionName: PropTypes.string,
        collectionImage: PropTypes.string,
        trackEvent: PropTypes.func,
        isGDPRCountry: PropTypes.bool.isRequired,
    };

    static defaultProps = {
        isFollowing: false,
    };

    state = {
        isLoading: false,
    };

    async componentDidMount() {
        const { userId, collectionId, isGDPRCountry } = this.props;

        if (userId) {
            if (this._isNewlyAuthenticatedUser() && this._hasPendingFollow()) {
                Cookies.expire(getPendingCookieName(collectionId));
                const shouldShowModal = await shouldShowGDPRModal(
                    isGDPRCountry,
                    userId,
                );
                if (shouldShowModal) {
                    this._handleGDPRFlow();
                }
                this._handleFollowCollection();
            }
        }
    }

    _hasPendingFollow = () =>
        Cookies.get(getPendingCookieName(this.props.collectionId));

    _isNewlyAuthenticatedUser = () => {
        const {
            location: { query },
            isFollowing,
        } = this.props;
        const isFromAuth =
            query &&
            (query['internal_ref'] === 'signup' ||
                query['internal_ref'] === 'login');

        return isFromAuth && !isFollowing;
    };

    _addPendingFollow = () => {
        const { collectionId } = this.props;

        Cookies.set(getPendingCookieName(collectionId), true, {
            expires: 3600,
        });
    };

    _handleUnauthenticatedUser = () => {
        const { location, collectionName, collectionImage } = this.props;

        setAction({
            action: 'followCollection',
            payload: {
                title: collectionName,
                image: collectionImage,
            },
        });
        this._addPendingFollow();
        setWindowLocation(
            `/signin/confirmation/?referrer=${location.pathname}`,
        );
    };

    _handleFollowCollection = () => {
        this.props.handleFollowCollection((isLoading) =>
            this.setState({ isLoading }),
        );
    };

    _handleUnfollowCollection = () => {
        this.props.handleUnfollowCollection((isLoading) =>
            this.setState({ isLoading }),
        );
    };

    _handleAccept = () => {
        const { hideOverlay, userId, trackEvent } = this.props;

        trackEvent(GDPR_ACCEPT_GA_ACTION);

        setFollowCollection(userId, true);
        hideOverlay();
    };

    _handleReject = () => {
        const { userId, hideOverlay, trackEvent } = this.props;

        trackEvent(GDPR_REJECT_GA_ACTION);

        setFollowCollection(userId, false);
        hideOverlay();
    };

    _handleGDPRFlow = () => {
        const { showOverlay } = this.props;

        showOverlay('dialog', {
            title: GDPR_TITLE_TEXT,
            message: GDPR_MESSAGE_TEXT,
            primaryText: GDPR_ACCEPT_TEXT,
            primaryType: 'link',
            onPrimaryAction: this._handleAccept,
            secondaryText: GDPR_REJECT_TEXT,
            secondaryType: 'link',
            onSecondaryAction: this._handleReject,
            onClose: this._handleReject,
        });
    };

    _handleFollowClick = async () => {
        const { isFollowing, userId, isGDPRCountry } = this.props;

        if (userId) {
            if (isFollowing) {
                this._handleUnfollowCollection();
            } else {
                const shouldShowModal = await shouldShowGDPRModal(
                    isGDPRCountry,
                    userId,
                );
                if (shouldShowModal) {
                    this._handleGDPRFlow();
                }
                this._handleFollowCollection();
            }
        } else {
            this._handleUnauthenticatedUser();
        }
    };

    render() {
        const { isFollowing, isPublic } = this.props;

        const containerClassNames = classnames(
            'user-collection__follow-btn-container',
            {
                'user-collection__follow-btn-has-not-followed': !isFollowing,
                'user-collection__follow-btn-has-followed ': isFollowing,
            },
        );

        return (
            <div className={containerClassNames}>
                {!isPublic && (
                    <span className="eds-align--center eds-text-bs eds-text-color--grey-600">
                        {gettext(
                            'This collection cannot be followed because it is not public',
                        )}
                    </span>
                )}
                <FollowButton
                    style="solitary"
                    isFollowing={isFollowing}
                    onClick={this._handleFollowClick}
                    disabled={!isPublic}
                />
            </div>
        );
    }
}

export default withOverlayControls(FollowCTA);
